<template>
  <v-container fluid>
    <page-bar :title="sectionTitle" />

    <!-- Filter Section -->
    <div class="px-6 py-4 section-search__filters" :class="getFilterStyle">
      <v-card
        ref="filterCard"
        dark
        color="primaryDark"
        tile
      >
        <v-card-text class="pa-4">
          <grid-system
            v-if="expanded && loadedSearchData"
            :columns="12"
            :gap="16"
          >
            <div class="span-6">
              <v-select
                v-model="selectedCategories"
                :items="searchData.categories"
                multiple
                clearable
                dense
                label="Filter by category"
                persistent-hint
                placeholder="Select a Category"
                hide-details
                v-on:change="Search"
                class="theme-dark"
                :menu-props="{ light: true }"
                item-color="white"
                item-text="name"
                return-object
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip small v-if="index < 3">
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <span v-if="index === 3"
                    >(+{{ selectedCategories.length - 1 }} others)</span
                  >
                </template>
              </v-select>
            </div>
            <div class="span-6">
              <v-text-field
                v-model="search"
                dense
                hide-details
                label="Search by name or identifier"
                v-on:change="OnSearchTextChange"
              ></v-text-field>
            </div>
            <template v-if="section && section.focusedAttributes">
              <div
                class="span-4"
                v-for="(focusedAttribute, index) in section.focusedAttributes"
                :key="index"
              >
                <attribute-field
                  :attribute="focusedAttribute"
                  v-on:OnChanged="OnAttributeFieldChange"
                  :categories="selectedCategories"
                  :defaultItems="
                    GetAttributeDataForExistingSearch(focusedAttribute)
                  "
                  :cascadingAttributes="selectedAttributes"
                  :isSearchAttribute="true"
                  :searchSectionId="searchData.sectionid"
                ></attribute-field>
              </div>
            </template>
            <div class="span-6" v-if="showFilters">
              <v-text-field
                v-model="filtersSearch"
                clearable
                flat
                solo-inverted
                hide-details
                label="Search attributes"
                dense
              ></v-text-field>
            </div>
            <div class="span-6" v-if="showFilters">
              <div class="d-flex justify-end align-center">
                <span class="mr-2">
                  Attributes : {{ (filterPage - 1) * itemsPerPage }} -
                  {{ filteredAttributes.length }}
                </span>
                <v-btn
                  icon
                  text
                  class="ma-0"
                  @click="filterPage != 1 ? filterPage-- : 0"
                >
                  <v-icon>
                    mdi-arrow-left-bold-circle
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  text
                  class="ma-0"
                  @click="
                    filterPage * itemsPerPage > filteredAttributes.length
                      ? 0
                      : filterPage++
                  "
                >
                  <v-icon>
                    mdi-arrow-right-bold-circle
                  </v-icon>
                </v-btn>
              </div>
            </div>
            <div class="span-12" v-if="showFilters">
              <v-data-iterator
                :items="filteredAttributes"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ itemsPerPageOptions }"
                :search="filtersSearch"
                hide-default-footer=""
                :page="filterPage"
              >
                <template v-slot:default="props">
                  <grid-system
                    :columns="{ xs: 2, sm: 4, md: 4, lg: 4 }"
                    :gap="8"
                  >
                    <div v-for="item in props.items" :key="item.id">
                      <attribute-field
                        :attribute="item"
                        v-on:OnChanged="OnAttributeFieldChange"
                        :categories="selectedCategories"
                        :defaultItems="GetAttributeDataForExistingSearch(item)"
                        :cascadingAttributes="selectedAttributes"
                        :isSearchAttribute="true"
                        :searchSectionId="searchData.sectionid"
                      ></attribute-field>
                    </div>
                  </grid-system>
                </template>
              </v-data-iterator>
            </div>
            <div class="span-12 pa-0 pb-1">
              <div class="d-flex align-center justify-end">
                <v-btn
                  v-if="selectedAssets.length"
                  height="28px"
                  class="pa-0 ma-0 ml-2"
                  width="150px"
                  color="green"
                  :loading="downloadingPDF"
                  @click="OnTapDownloadMultiplePDF()"
                >
                  <span style="font-size:10px">
                    Download as PDF
                  </span>
                </v-btn>
                <v-btn
                  v-if="selectedAssets.length"
                  height="28px"
                  class="pa-0 ma-0 ml-2"
                  width="150px"
                  color="red"
                  @click="DeleteAssets()"
                >
                  <span style="font-size:10px">
                    Delete Assets
                  </span>
                </v-btn>
                <v-btn
                  v-if="selectedAssets.length"
                  height="28px"
                  class="pa-0 ma-0 ml-2"
                  width="150px"
                  color="orange"
                  @click="ClearSelection()"
                >
                  <span style="font-size:10px">
                    Clear Selection
                  </span>
                </v-btn>

                <v-btn
                  v-if="selectedAssets.length && inventoryActive"
                  height="28px"
                  class="pa-0 ma-0 ml-2"
                  width="130px"
                  color="green"
                  @click="AddSelectedToCurrentAssetList()"
                >
                  <span style="font-size:10px">
                    Add to Asset List
                  </span>
                </v-btn>
                <v-btn
                  v-if="selectedCategories.length === 1"
                  height="28px"
                  class="pa-0 ma-0 ml-2 section-search__internal-copy-btn"
                  width="250px"
                  color="blue"
                  :loading="internalImageCopyInProgress"
                  @click="OnTapInternalImageCopyManifest()"
                >
                  <span class="section-search__internal-copy-btn-text">
                    Internal Copy Images + Manifest
                  </span>
                </v-btn>
                <v-btn
                  text
                  height="28px"
                  class="pa-0 ma-0 mr-2"
                  width="150px"
                  @click="FilterButtonPress()"
                >
                  <span style="font-size:10px">
                    {{ showFilters ? "Hide" : "Show" }} Filters
                  </span>
                </v-btn>
              </div>
            </div>
          </grid-system>

          <Loading v-if="expanded && !loadedSearchData"></Loading>
        </v-card-text>
      </v-card>
    </div>

    <!-- Assets Section -->
    <div class="px-6 py-4">
      <grid-system
        v-if="loadedSearchData"
        :columns="{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }"
        :gap="16"
      >
        <template v-if="searchResults.results.length > 0">
          <AssetCard
            v-for="(asset, index) in searchResults.results"
            :key="index"
            :isUrlLink="true"
            :asset="asset"
            :showSelect="userCanEdit"
            v-on:AssetSelected="OnAssetSelected"
            :selected="selectedAssets.indexOf(asset.id) > -1"
          ></AssetCard>
        </template>
        <div v-else>
          <!-- Empty state -->
        </div>
      </grid-system>

      <!-- Infinite loading outside of grid -->
      <div v-if="loadedSearchData" class="mt-4">
        <infinite-loading
          :identifier="infiniteIncrementor"
          @infinite="infiniteHandler"
        >
          <div slot="spinner">Loading...</div>
          <div slot="no-more"></div>
          <div slot="no-results"></div>
        </infinite-loading>
      </div>

      <div v-else class="d-flex justify-center align-center">
        <Loading></Loading>
      </div>
    </div>

    <!-- Delete Assets Confirmation Dialog -->
    <v-dialog
      v-model="showDeleteAssetsDialog"
      width="500px"
      height="200px"
      v-if="CanUserEditAssets()"
      persistent
    >
      <v-card>
        <v-card-title class="pa-3 headline">Delete Assets</v-card-title>

        <v-card-text class="cardcontent">
          <v-row class="subtitle-1">
            <span
              >Are you sure you want to delete
              {{ selectedAssets.length }} asset(s)?</span
            >
          </v-row>
          <v-row>
            This action cannot be undone.
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            style="width:100px"
            color="green darken-1"
            dark
            @click="showDeleteAssetsDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            style="width:150px"
            color="red darken-1"
            dark
            @click="ConfirmDeleteAssets"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showInternalImageCopyDialog"
      width="580px"
      height="220px"
      persistent
    >
      <v-card>
        <v-card-title class="pa-3 headline">Internal Image Copy Tool</v-card-title>

        <v-card-text class="cardcontent">
          <v-row v-if="!internalImageCopyCompleted" class="subtitle-1">
            <span>
              This will copy image resources for category
              <strong>{{ selectedCategories[0] ? selectedCategories[0].name : "" }}</strong>
              in the current section, and write a JSON manifest.
            </span>
          </v-row>
          <v-row v-if="!internalImageCopyCompleted">
            Destination tenant/container is hard-coded server-side for internal use.
          </v-row>
          <v-row v-if="internalImageCopyCompleted" class="mt-1">
            <div
              class="section-search__internal-copy-result"
              :class="{
                'section-search__internal-copy-result--success': !internalImageCopyCompletedWithErrors,
                'section-search__internal-copy-result--error': internalImageCopyCompletedWithErrors,
              }"
            >
              <div class="section-search__internal-copy-result-title">
                {{
                  internalImageCopyCompletedWithErrors
                    ? "Copy completed with errors"
                    : "Copy completed successfully"
                }}
              </div>
              <div class="section-search__internal-copy-result-body">
                {{ internalImageCopyStatusText }}
              </div>
            </div>
          </v-row>
          <v-row
            v-else-if="internalImageCopyStatusText"
            class="mt-2"
          >
            <span class="section-search__internal-copy-status">{{ internalImageCopyStatusText }}</span>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            style="width:100px"
            color="grey darken-1"
            dark
            :disabled="internalImageCopyInProgress"
            @click="showInternalImageCopyDialog = false"
          >
            {{
              internalImageCopyCompleted
                ? "Done"
                : internalImageCopyInProgress
                  ? "Cancel"
                  : "Close"
            }}
          </v-btn>

          <v-btn
            v-if="!internalImageCopyCompleted"
            style="width:170px"
            color="blue darken-1"
            dark
            :loading="internalImageCopyInProgress"
            @click="ConfirmInternalImageCopyManifest"
          >
            Confirm + Start
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Loading from "../../shared/Loading";
import { mapMutations, mapActions, mapState, mapGetters } from "vuex";
import AttributeField from "../../attributes/AttributeField";
import InfiniteLoading from "vue-infinite-loading";
import AssetCard from "../../cards/AssetCard";
import PageBar from "../../shared/PageBar";
import GridSystem from "../../shared/GridSystem";

export default {
  components: {
    Loading,
    AttributeField,
    InfiniteLoading,
    AssetCard,
    PageBar,
    GridSystem,
  },
  props: {},
  computed: {
    ...mapState("assetxpstate", [
      "searchResults",
      "searchInProgress",
      "section",
      "searchData",
      "prevSearchedSection",
      "inventoryActive",
      "savedSectionSearches",
      "markets",
    ]),
    ...mapGetters("assetxpstate", ["effectiveMarketId"]),
    ...mapState("navstate", ["selectedSection", "drawOpen"]),
    sectionTitle() {
      return this.section && this.section.name
        ? this.section.name + " Search"
        : "Section Search";
    },
    getFilterStyle() {
      const baseClass = "filter-card";
      const drawerClass = this.drawOpen
        ? "filter-card--drawer-open"
        : "filter-card--drawer-closed";
      const inventoryClass = this.inventoryActive
        ? "filter-card--inventory-active"
        : "filter-card--inventory-inactive";

      return `${baseClass} ${drawerClass} ${inventoryClass}`;
    },
    width() {
      return 5;
    },
    filteredAttributes() {
      var attributesListWithoutMains = [];

      // Guard against null section or missing data
      if (!this.searchData?.attributes || !this.section?.focusedAttributes) {
        return attributesListWithoutMains;
      }

      for (let i = 0; i < this.searchData.attributes.length; i++) {
        const element = this.searchData.attributes[i];

        var matches = this.section.focusedAttributes.filter(
          (e) => e.id == element.id
        );

        if (matches.length == 0 && element.attributeType == 3) {
          attributesListWithoutMains.push(element);
        }
      }

      return attributesListWithoutMains;
    },
    userCanEdit() {
      return this.CanUserEditAssets();
    },
  },
  mounted: function() {
    this.LoadSectionSearch();
  },
  beforeDestroy() {
    this.StopInternalImageCopyPolling();
  },
  data: () => ({
    performingSearch: false,
    filterPage: 1,
    infiniteIncrementor: 0,
    loadedSearchData: false,
    isLoadingSectionSearch: false,
    pendingSectionSearchReload: false,
    searchParams: {},
    filtersSearch: "",
    search: "",
    textToSearch: "",
    prevSearch: "",
    expanded: true,
    showFilters: false,
    itemsPerPageOptions: [2, 4, 8],
    itemsPerPage: 8,
    items: [],
    selectedCategories: [],
    states: [
      "Alabama",
      "Alaska",
      "American Samoa",
      "Arizona",
      "Arkansas",
      "California",
      "Colorado",
      "Connecticut",
    ],
    selectedAttributes: [],
    selectedAssets: [],
    prevSearchparams: {},
    lastRunSearchQuery: null,
    downloadingPDF: false,
    showDeleteAssetsDialog: false,
    showInternalImageCopyDialog: false,
    internalImageCopyInProgress: false,
    internalImageCopyJobId: null,
    internalImageCopyStatusText: "",
    internalImageCopyPollHandle: null,
    internalImageCopyManifestDownloaded: false,
    internalImageCopyCompleted: false,
    internalImageCopyCompletedWithErrors: false,
  }),
  methods: {
    ...mapGetters("authentication", ["CanUserEditAssets"]),
    ...mapActions("navstate", ["GetSelectedSection"]),
    ...mapActions("assetxpstate", [
      "GetSection",
      "ResetSelectedAsset",
      "RemoveMultipleAssets",
      "DownloadPDFMultiple",
      "StartInternalImageCopyManifestJob",
      "GetInternalImageCopyManifestJobStatus",
      "DownloadInternalImageCopyManifest",
    ]),
    ...mapMutations("assetxpstate", [
      "AddAssetsToCurrentAssetList",
      "UpsertCachedSectionSearch",
      "ClearSectionSearchFromCache",
      "ClearAllSectionSearchFromCache",
    ]),

    ClearSelection() {
      this.selectedAssets.splice(0, this.selectedAssets.length);
    },
    FilterButtonPress() {
      this.showFilters = !this.showFilters;
    },
    AddSelectedToCurrentAssetList() {
      this.AddAssetsToCurrentAssetList(this.selectedAssets);
    },
    OnTextSearchChange() {
      if (this.prevSearch != this.search) {
        this.prevSearch = this.search;
        this.Search();
      }
    },
    async infiniteHandler($state) {
      // Guard against duplicate calls while search is in progress
      if (this.performingSearch) {
        return;
      }

      const resolvedSectionId = this.searchData?.sectionid || this.section?.id || null;
      if (!resolvedSectionId) {
        try {
          await this.GetSearchData(this.$route.params["sectionName"]);
        } catch (e) {
          console.warn("[SectionSearch] GetSearchData failed before search dispatch", e);
        }
      }

      const sectionIdForSearch = this.searchData?.sectionid || this.section?.id || null;
      if (!sectionIdForSearch) {
        console.warn("[SectionSearch] Skipping search dispatch: missing sectionid", {
          sectionName: this.$route.params["sectionName"],
        });
        $state.complete();
        return;
      }

      let catids = new Array();
      this.performingSearch = true;

      for (let i = 0; i < this.selectedCategories.length; i++) {
        catids.push(this.selectedCategories[i].id);
      }

      const pageCount = 24;
      const loadedResultsCount = Array.isArray(this.searchResults.results)
        ? this.searchResults.results.length
        : 0;
      const nextPageNumber =
        loadedResultsCount > 0
          ? Math.floor(loadedResultsCount / pageCount) + 1
          : 1;

      var searchParams = {
        categories: catids,
        textSearch: this.search,
        attributes: this.selectedAttributes,
        token:
          Array.isArray(this.searchResults.results) &&
          this.searchResults.results.length === 0
            ? ""
            : this.searchResults.token,
        sectionid: sectionIdForSearch,
        sectionName: this.$route.params["sectionName"],
        pageNumber: nextPageNumber,
        pageCount,
      };

      this.lastRunSearchQuery = searchParams;

      const hasMultipleMarkets =
        Array.isArray(this.markets) && this.markets.length > 1;

      if (hasMultipleMarkets && this.effectiveMarketId) {
        searchParams.marketId = this.effectiveMarketId;
      }

      const searchAction = hasMultipleMarkets && this.effectiveMarketId
        ? this.GetSearchResultsByMarketId
        : this.GetSearchResults;

      console.log("[MarketTrace][SectionSearch] search dispatch", {
        hasMultipleMarkets,
        selectedMarketId: this.$store.state.assetxpstate.selectedMarketId,
        effectiveMarketId: this.effectiveMarketId,
        endpoint:
          searchAction === this.GetSearchResultsByMarketId
            ? "asset/cross-market-search"
            : "asset/search",
        marketIdInPayload: searchParams.marketId,
        section: searchParams.sectionName,
      });

      searchAction(searchParams)
        .then(() => {
          this.performingSearch = false;
          if (this.searchResults.hasNextPage) {
            $state.loaded();
          } else {
            $state.complete();
          }
        })
        .catch(() => {
          // Ensure flag is reset on error
          this.performingSearch = false;
          $state.complete();
        });
    },
    OnAssetSelected(eventObj) {
      var listingid = eventObj.id;
      var value = eventObj.value;
      var index = this.selectedAssets.indexOf(listingid);

      //if asset is not selected but was previously in the selected list
      if (value === false && index >= 0) {
        this.selectedAssets.splice(index, 1);
      }

      //if asset is selected and is not already in the selected list
      if (value === true && index < 0) {
        this.selectedAssets.push(listingid);
      }
    },
    DeleteAssets() {
      this.showDeleteAssetsDialog = true;
    },
    ConfirmDeleteAssets() {
      this.RemoveMultipleAssets({ ids: this.selectedAssets }).then(() => {
        this.showDeleteAssetsDialog = false;
        this.$router.push({
          name: "Section",
          params: { sectionName: this.section.name },
        });
      });
    },
    OnAttributeFieldChange(eventObj) {
      let foundAttribute = false;

      for (let i = 0; i < this.selectedAttributes.length; i++) {
        if (this.selectedAttributes[i].id == eventObj.attribute.id) {
          if (eventObj.values.length == 0) {
            this.selectedAttributes.splice(i);
          } else if (eventObj.values[0] == "") {
            this.selectedAttributes.splice(i);
          } else {
            this.selectedAttributes[i].values = eventObj.values;
            this.selectedAttributes[i].names = eventObj.names;
          }

          foundAttribute = true;
          break;
        }
      }

      if (!foundAttribute) {
        if (eventObj.values.length != 0) {
          if (eventObj.values[0] != "") {
            //We do not, therefore lets add it with the current values
            this.selectedAttributes.push({
              id: eventObj.attribute.id,
              attributeType: eventObj.attribute.attributeType,
              values: eventObj.values,
              names: eventObj.names,
            });
          }
        }
      }

      this.Search();
    },
    OnSearchTextChange() {
      this.textToSearch = this.search;
    },
    Search() {
      let catids = new Array();

      for (let i = 0; i < this.selectedCategories.length; i++) {
        catids.push(this.selectedCategories[i].id);
      }

      const sectionIdForSearch = this.searchData?.sectionid || this.section?.id || null;
      if (!sectionIdForSearch) {
        console.warn("[SectionSearch] Search called before sectionid was ready", {
          sectionName: this.$route.params["sectionName"],
        });
        return;
      }

      var searchParams = {
        categories: catids,
        textSearch: this.search,
        attributes: this.selectedAttributes,
        token: "",
        sectionid: sectionIdForSearch,
        sectionName: this.$route.params["sectionName"],
        pageNumber: 1,
        pageCount: 24,
      };

      this.lastRunSearchQuery = searchParams;

      this.ClearSearch();
      this.infiniteIncrementor += 1;

      /*    Not handled correctly yet, we need to check if values have changed!
      if(this.prevSearchparams.categories != null)
      {
        if(this.prevSearchparams.categories.length == searchParams.categories.length &&
          this.prevSearchparams.textSearch == searchParams.textSearch &&
          this.prevSearchparams.attributes.length == searchParams.attributes.length &&
          this.prevSearchparams.sectionid == searchParams.sectionid &&
          this.prevSearchparams.sectionName == searchParams.sectionName)
          {
            //No search, this is the same
          }
        else
          {
            this.ClearSearch();
            this.infiniteIncrementor += 1;
          }
      }
      else
      {
        this.ClearSearch();
        this.infiniteIncrementor += 1;
      }
*/

      this.prevSearchparams = searchParams;
    },
    getCurrentSectionRouteKey() {
      const sectionName = this.$route?.params?.sectionName || "";
      const category = this.$route?.params?.category || "";
      return `${sectionName}::${category}`;
    },
    async LoadSectionSearch() {
      const routeKey = this.getCurrentSectionRouteKey();
      const sectionName = this.$route.params["sectionName"];
      const category = this.$route.params["category"];

      // Queue the latest route change so fast section switching cannot drop updates.
      if (this.isLoadingSectionSearch) {
        this.pendingSectionSearchReload = true;
        return;
      }

      this.isLoadingSectionSearch = true;
      this.pendingSectionSearchReload = false;

      try {
        // First-load hardening: if token tenant is stale, fix it before Product queries run.
        await this.ensureWorkspaceTokenFresh();

        // Ensure markets are loaded and effectiveMarketId is resolved before searching
        if (!this.markets || this.markets.length === 0) {
          await this.GetMyMarkets();
        }

        this.GetSelectedSection(sectionName);
        this.GetSection(sectionName);

        if (this.asset !== null) {
          this.ResetSelectedAsset();
        }

        this.ClearSearch();
        if (
          this.prevSearchedSection != sectionName ||
          category != null
        ) {
          this.loadedSearchData = false;
          this.ClearSearch();
          await this.GetSearchData(sectionName);

          if (this.getCurrentSectionRouteKey() !== routeKey) {
            return;
          }

          this.selectedCategories = [];
          this.selectedAttributes = [];
          this.selectedAssets = [];

          if (category != null) {
            var item = this.searchData.categories.filter(
              (e) => e.id == category
            );

            if (item.length > 0) this.selectedCategories.push(item[0]);
          }

          this.PopulateForExistingSearchData();

          this.loadedSearchData = true;
          return;
        }

        this.PopulateForExistingSearchData();
        this.loadedSearchData = true;
      } catch (err) {
        console.error("Failed to load section search data:", err);
        this.loadedSearchData = true;
      } finally {
        this.isLoadingSectionSearch = false;

        if (
          this.pendingSectionSearchReload ||
          this.getCurrentSectionRouteKey() !== routeKey
        ) {
          this.pendingSectionSearchReload = false;
          this.$nextTick(() => {
            this.LoadSectionSearch();
          });
        }
      }
    },

    ...mapMutations("assetxpstate", ["ClearSearch"]),
    ...mapActions("assetxpstate", [
      "GetSearchResults",
      "GetSearchResultsByMarketId",
      "GetSection",
      "GetSearchData",
      "GetMyMarkets",
    ]),

    async ensureWorkspaceTokenFresh() {
      // Temporary kill switch (see AppBar.vue's hideWorkspaceAndMarketSelectors, App.vue's
      // applyWorkspaceCrossAppSync, and docs/workspace-market-switching-kill-switch.md):
      // workspace/market switching is hard-disabled, so there is no stale-tenant drift for this
      // to correct — skip it rather than calling /api/workspace/ping on every section load/route
      // change. To re-enable, restore the code this replaced (see git history for this file) and
      // remove this comment.
      return;
    },

    PopulateForExistingSearchData() {
      if (!this.section?.id) return;
      //Now lets fill the remaining values with any existing search data
      var existingSearchData = this.savedSectionSearches.filter(
        (e) => e && this.section && e.id == this.section.id
      );
      if (existingSearchData.length > 0) {
        const formerSearchData = existingSearchData[0].searchData;
        //CATEGORIES
        this.selectedCategories = [];
        formerSearchData.categories.forEach((formerCategory) => {
          var categoryMatch = this.searchData.categories.filter(
            (e) => e.id == formerCategory
          );
          if (categoryMatch.length > 0)
            this.selectedCategories.push(categoryMatch[0]);
        });

        this.search = formerSearchData.textSearch;
        this.selectedAttributes = formerSearchData.attributes;
      }
    },
    GetAttributeDataForExistingSearch(attribute) {
      if (!this.section?.id) return null;
      var existingSearchData = this.savedSectionSearches.filter(
        (e) => e && this.section && e.id == this.section.id
      );
      if (existingSearchData.length > 0) {
        const formerSearchData = existingSearchData[0].searchData;
        var existingAttributeData = formerSearchData.attributes.filter(
          (e) => e.id == attribute.id
        );

        if (existingAttributeData.length > 0) return existingAttributeData[0];
        else return null;
      }
    },
    OnAssetPress(assetDetailId) {
      this.$router.push("/asset/" + assetDetailId);
    },
    OnTapDownloadMultiplePDF() {
      this.downloadingPDF = true;

      this.DownloadPDFMultiple(this.selectedAssets).then(() => {
        this.downloadingPDF = false;
      });
    },
    OnTapInternalImageCopyManifest() {
      if (!this.selectedCategories || this.selectedCategories.length !== 1) {
        this.internalImageCopyStatusText =
          "Select exactly one category before starting this tool.";
        this.showInternalImageCopyDialog = true;
        return;
      }

      this.internalImageCopyStatusText = "";
      this.internalImageCopyManifestDownloaded = false;
      this.internalImageCopyCompleted = false;
      this.internalImageCopyCompletedWithErrors = false;
      this.showInternalImageCopyDialog = true;
    },
    async ConfirmInternalImageCopyManifest() {
      if (!this.section || !this.section.id || this.selectedCategories.length !== 1) {
        this.internalImageCopyStatusText =
          "Missing section/category context. Refresh and try again.";
        return;
      }

      this.internalImageCopyInProgress = true;
      this.internalImageCopyManifestDownloaded = false;
  this.internalImageCopyCompleted = false;
  this.internalImageCopyCompletedWithErrors = false;
      this.internalImageCopyStatusText = "Submitting job...";

      try {
        const selectedCategory = this.selectedCategories[0];
        const response = await this.StartInternalImageCopyManifestJob({
          sectionId: this.section.id,
          categoryId: selectedCategory.id,
          categoryName: selectedCategory.name,
        });

        this.internalImageCopyJobId = response && response.jobId ? response.jobId : null;
        if (!this.internalImageCopyJobId) {
          throw new Error("No job id returned from API.");
        }

        this.internalImageCopyStatusText = `Job started (${this.internalImageCopyJobId}). Polling status...`;
        this.StartInternalImageCopyPolling();
      } catch (error) {
        const apiMessage =
          error?.response?.data || error?.message || "Failed to start copy job.";
        this.internalImageCopyStatusText = String(apiMessage);
        this.internalImageCopyInProgress = false;
      }
    },
    IsInternalImageCopyTerminalStatus(status) {
      const completion = status?.completitionState;
      const normalizedCompletion = String(completion ?? "").toUpperCase();
      const normalizedMessage = String(status?.statusMessage || "").toUpperCase();
      const percentComplete = Number(status?.percentComplete);

      if (
        normalizedCompletion === "SUCCESS" ||
        normalizedCompletion === "ERROR" ||
        normalizedCompletion === "COMPLETED" ||
        completion === 1 ||
        completion === 2
      ) {
        return true;
      }

      if (
        normalizedMessage.includes("COMPLETED") ||
        normalizedMessage === "FAILED" ||
        normalizedMessage.includes("SUCCESS") ||
        normalizedMessage.includes("ERROR")
      ) {
        return true;
      }

      return Number.isFinite(percentComplete) && percentComplete >= 100;
    },
    IsInternalImageCopyErrorStatus(status) {
      const completion = status?.completitionState;
      const normalizedCompletion = String(completion ?? "").toUpperCase();
      const normalizedMessage = String(status?.statusMessage || "").toUpperCase();

      return (
        completion === 2 ||
        normalizedCompletion === "ERROR" ||
        normalizedMessage === "FAILED" ||
        normalizedMessage.includes("ERROR")
      );
    },
    StartInternalImageCopyPolling() {
      this.StopInternalImageCopyPolling();

      this.internalImageCopyPollHandle = setInterval(async () => {
        if (!this.internalImageCopyJobId) return;

        try {
          const status = await this.GetInternalImageCopyManifestJobStatus(
            this.internalImageCopyJobId
          );

          const message = status?.statusMessage || "Processing...";
          const percent = Number.isFinite(status?.percentComplete)
            ? `${Math.round(status.percentComplete)}%`
            : "";
          const logs = Array.isArray(status?.baseClientJob?.logs)
            ? status.baseClientJob.logs
            : [];
          const latestLog = logs.length > 0 ? logs[logs.length - 1] : "";
          const baseStatusText =
            percent.length > 0 ? `${message} (${percent})` : message;
          this.internalImageCopyStatusText = latestLog
            ? `${baseStatusText} - ${latestLog}`
            : baseStatusText;

          if (this.IsInternalImageCopyTerminalStatus(status)) {
            this.internalImageCopyInProgress = false;
            this.internalImageCopyCompleted = true;
            this.internalImageCopyCompletedWithErrors = this.IsInternalImageCopyErrorStatus(status);
            this.StopInternalImageCopyPolling();

            const completionText =
              this.IsInternalImageCopyErrorStatus(status)
                ? "Job completed with errors."
                : "Job completed successfully.";

            if (!this.internalImageCopyManifestDownloaded && this.internalImageCopyJobId) {
              try {
                await this.DownloadInternalImageCopyManifest(
                  this.internalImageCopyJobId
                );
                this.internalImageCopyManifestDownloaded = true;
                this.internalImageCopyStatusText = `${completionText} Manifest downloaded to your computer.`;
              } catch (downloadError) {
                this.internalImageCopyStatusText = `${completionText} Manifest download failed.`;
              }
            } else {
              this.internalImageCopyStatusText = completionText;
            }
          }
        } catch (error) {
          this.internalImageCopyStatusText =
            "Polling failed. You can retry status refresh by reopening this dialog.";
          this.internalImageCopyInProgress = false;
          this.StopInternalImageCopyPolling();
        }
      }, 2500);
    },
    StopInternalImageCopyPolling() {
      if (this.internalImageCopyPollHandle) {
        clearInterval(this.internalImageCopyPollHandle);
        this.internalImageCopyPollHandle = null;
      }
    },
  },
  watch: {
    $route() {
      this.StopInternalImageCopyPolling();
      this.prevSearchparams = {};
      this.selectedCategories = [];
      this.selectedAttributes = [];
      this.selectedAssets = [];
      this.LoadSectionSearch();
    },
    lastRunSearchQuery: {
      handler(newVal) {
        if (!this.section?.id) return;
        this.UpsertCachedSectionSearch({
          id: this.section.id,
          searchData: newVal,
        });
      },
    },

    textToSearch: {
      handler() {
        this.Search();
      },
    },
    effectiveMarketId(newMarketId, oldMarketId) {
      // Reset all search filters when effective market context changes
      if (newMarketId !== oldMarketId && this.loadedSearchData) {
        // Reset search filters
        this.selectedCategories = [];
        this.search = "";
        this.selectedAttributes = [];

        // Clear search results and restart infinite loader
        this.ClearSearch();
        this.infiniteIncrementor += 1;

        // Clear cached search data for this section (market-specific)
        if (this.section && this.section.id) {
          this.ClearSectionSearchFromCache(this.section.id);
        }

        // Re-fetch search data for the new market so the category filter
        // (populated from searchData.categories) repopulates.
        const sectionName = this.$route.params["sectionName"];
        if (sectionName) {
          this.loadedSearchData = false;
          this.GetSearchData(sectionName).then(() => {
            this.loadedSearchData = true;
            this.Search();
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/styles/FilterCard.scss";

.section-search__filters {
  background: transparent;
}

.section-search__internal-copy-btn {
  min-width: 250px;
}

.section-search__internal-copy-btn-text {
  display: inline-block;
  font-size: 10px;
  line-height: 1.1;
  white-space: normal;
  text-align: center;
  max-width: 220px;
}

.section-search__internal-copy-status {
  display: inline-block;
  line-height: 1.4;
}

.section-search__internal-copy-result {
  width: 100%;
  border-radius: 8px;
  padding: 14px 16px;
  border: 1px solid transparent;
}

.section-search__internal-copy-result--success {
  background: #e8f5e9;
  border-color: #81c784;
  color: #1b5e20;
}

.section-search__internal-copy-result--error {
  background: #ffebee;
  border-color: #e57373;
  color: #b71c1c;
}

.section-search__internal-copy-result-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
}

.section-search__internal-copy-result-body {
  line-height: 1.45;
}
</style>
